import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import adminAuthService from '../services/adminAuthService';
import { LogOut, User, Shield, Users, Car } from 'lucide-react';

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { admin, logout } = useAuthStore();

  const handleLogout = async () => {
    try {
      await adminAuthService.logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      logout();
      navigate('/login');
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'SUPER_ADMIN': return '#ef4444';
      case 'ADMIN': return '#f59e0b';
      case 'CUSTOMER_SUPPORT': return '#3b82f6';
      case 'MODERATOR': return '#8b5cf6';
      case 'ANALYST': return '#10b981';
      case 'FINANCE': return '#06b6d4';
      default: return '#6b7280';
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      padding: '40px 20px',
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px',
          }}>
            <div>
              <h1 style={{
                fontSize: '32px',
                fontWeight: '700',
                color: '#0f172a',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}>
                <Shield size={32} style={{ color: '#3b82f6' }} />
                Admin Dashboard
              </h1>
              <p style={{ fontSize: '16px', color: '#64748b', margin: 0 }}>
                Welcome back, {admin?.firstName} {admin?.lastName}!
              </p>
            </div>
            <button
              onClick={handleLogout}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 24px',
                background: '#ef4444',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
              onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
            >
              <LogOut size={18} />
              Logout
            </button>
          </div>
        </div>

        {/* Quick Actions */}
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '32px',
          marginBottom: '24px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        }}>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '20px',
          }}>
            Quick Actions
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
          }}>
            {/* All Users */}
            <button
              onClick={() => navigate('/users')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '20px 24px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(59, 130, 246, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(59, 130, 246, 0.3)';
              }}
            >
              <Users size={24} />
              All Users
            </button>

            {/* Drivers */}
            <button
              onClick={() => navigate('/drivers')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '20px 24px',
                background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(236, 72, 153, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(236, 72, 153, 0.3)';
              }}
            >
              <Car size={24} />
              Drivers
            </button>

            {/* Passengers */}
            <button
              onClick={() => navigate('/passengers')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '20px 24px',
                background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(6, 182, 212, 0.4)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(6, 182, 212, 0.3)';
              }}
            >
              <Users size={24} />
              Passengers
            </button>
          </div>
        </div>

        {/* Profile Card */}
        <div style={{
          background: 'white',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: '#0f172a',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}>
            <User size={24} style={{ color: '#3b82f6' }} />
            Your Profile
          </h2>

          <div style={{
            display: 'grid',
            gap: '20px',
          }}>
            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#64748b',
                display: 'block',
                marginBottom: '8px',
              }}>
                Name
              </label>
              <div style={{
                fontSize: '16px',
                color: '#0f172a',
                padding: '12px 16px',
                background: '#f8fafc',
                borderRadius: '8px',
              }}>
                {admin?.firstName} {admin?.lastName}
              </div>
            </div>

            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#64748b',
                display: 'block',
                marginBottom: '8px',
              }}>
                Email
              </label>
              <div style={{
                fontSize: '16px',
                color: '#0f172a',
                padding: '12px 16px',
                background: '#f8fafc',
                borderRadius: '8px',
              }}>
                {admin?.email}
              </div>
            </div>

            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#64748b',
                display: 'block',
                marginBottom: '8px',
              }}>
                Role
              </label>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: getRoleBadgeColor(admin?.adminRole || ''),
                color: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
              }}>
                {admin?.adminRole?.replace('_', ' ')}
              </div>
            </div>

            <div>
              <label style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#64748b',
                display: 'block',
                marginBottom: '8px',
              }}>
                Status
              </label>
              <div style={{
                display: 'inline-block',
                padding: '8px 16px',
                background: admin?.status === 'active' ? '#10b981' : '#6b7280',
                color: 'white',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
              }}>
                {admin?.status?.toUpperCase()}
              </div>
            </div>

            {admin?.department && (
              <div>
                <label style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#64748b',
                  display: 'block',
                  marginBottom: '8px',
                }}>
                  Department
                </label>
                <div style={{
                  fontSize: '16px',
                  color: '#0f172a',
                  padding: '12px 16px',
                  background: '#f8fafc',
                  borderRadius: '8px',
                }}>
                  {admin.department}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          marginTop: '32px',
          color: 'rgba(255, 255, 255, 0.7)',
          fontSize: '14px',
        }}>
          <p>✅ Authentication successful! You are now logged in as an admin.</p>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;